/**
 * Retreat Booking Form
 * ────────────────────
 * Renders booking forms for retreat cart items.
 * All forms are appended inside #retreat_sections_wrap.
 * State persisted in localStorage, synced to Shopify cart attributes.
 *
 * Sections:
 *  1. Config & Constants
 *  2. State Management
 *  3. Validation
 *  4. Field Rendering
 *  5. Form HTML Builder
 *  6. Event Handling
 *  7. PDF / Download
 *  8. Cart Sync
 *  9. DOM Rendering & Lifecycle
 */
(function () {
  'use strict';

  /* ================================================================
     1. CONFIG & CONSTANTS
     ================================================================ */

  const STORAGE_KEY = 'retreatBookingState';
  const WRAPPER_SELECTOR = '#retreat_sections_wrap';
  const FORM_ATTR = 'data-retreat-form';
  const FORM_SELECTOR = `[${FORM_ATTR}]`;
  const CART_SYNC_DELAY = 300;
  const POLL_BASE_DELAY = 150;
  const POLL_INCREMENT = 20;
  const POLL_MAX_ATTEMPTS = 20;

  const FIELDS = [
    { id: 'first-name', label: 'First Name', type: 'text', half: true },
    { id: 'last-name',  label: 'Last Name',  type: 'text', half: true },
    { id: 'address',    label: 'Address',     type: 'text' },
    { id: 'zip',        label: 'ZIP Code',    type: 'text', half: true },
    { id: 'city',       label: 'City',        type: 'text', half: true },
    {
      id: 'country', label: 'Country', type: 'select',
      options: [
        '', 'Austria', 'Germany', 'Switzerland', 'Italy',
        'France', 'United Kingdom', 'Netherlands', 'Belgium',
        'Spain', 'Portugal', 'Other',
      ],
    },
    {
      id: 'dob', label: 'Date of Birth', type: 'text', half: true,
      placeholder: 'dd.mm.yyyy', pattern: '\\d{2}\\.\\d{2}\\.\\d{4}', maxlength: 10,
    },
    { id: 'email', label: 'Email', type: 'email', half: true },
    { id: 'phone', label: 'Phone', type: 'tel' },
  ];

  const TERMS_LINKS = {
    terms:    '/policies/terms-of-service',
    infoSheet: 'https://cdn.shopify.com/s/files/1/0976/9880/8195/files/standard_information.pdf?v=1770933661',
    travel:   'https://deepblueconnection.com/products/dahab-immersion-ocean-retreat#f36a41cf-6a80-487a-93f9-db713fb2d29d,6ca4d862-54ee-494e-9003-d3c33c32d6d2,checkbox',
    package:  '',
  };


  /* ================================================================
     2. STATE MANAGEMENT
     ================================================================ */

  let STATE = null;

  function getState() {
    if (!STATE) {
      try {
        STATE = JSON.parse(localStorage.getItem(STORAGE_KEY)) || { forms: {} };
      } catch {
        STATE = { forms: {} };
      }
    }
    return STATE;
  }

  function saveState() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(STATE));
    } catch { /* quota exceeded — fail silently */ }
  }

  function getFormData(formId) {
    const state = getState();
    if (!state.forms[formId]) {
      state.forms[formId] = {
        fields: Object.fromEntries(FIELDS.map(f => [f.id, ''])),
        termsAccepted: false,
      };
      saveState();
    }
    return state.forms[formId];
  }

  function removeFormData(formId) {
    const state = getState();
    delete state.forms[formId];
    saveState();
  }


  /* ================================================================
     3. VALIDATION
     ================================================================ */

  function isValidDob(value) {
    if (!/^\d{2}\.\d{2}\.\d{4}$/.test(value)) return false;
    const [d, m, y] = value.split('.').map(Number);
    const date = new Date(y, m - 1, d);
    return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
  }

  function isFieldValid(field, value) {
    const v = (value || '').trim();
    if (!v) return false;
    if (field.id === 'dob')   return isValidDob(v);
    if (field.id === 'email') return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    return true;
  }

  function isFormComplete(formId) {
    const data = getFormData(formId);
    return data.termsAccepted && FIELDS.every(f => isFieldValid(f, data.fields[f.id]));
  }

  function getErrorMessage(field, value) {
    const v = (value || '').trim();
    if (!v) return `${field.label} is required`;
    if (field.id === 'dob' && !isValidDob(v))                         return 'Please enter a valid date (dd.mm.yyyy)';
    if (field.id === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'Please enter a valid email address';
    return '';
  }

  function showFieldError(wrapper, field, data) {
    const container = wrapper.querySelector(`[data-field="${field.id}"]`)?.closest('.form-field');
    if (!container) return;
    container.querySelector('.field-error')?.remove();

    const msg = getErrorMessage(field, data.fields[field.id]);
    if (msg) {
      container.classList.add('has-error');
      const span = document.createElement('span');
      span.className = 'field-error';
      span.textContent = msg;
      container.appendChild(span);
    } else {
      container.classList.remove('has-error');
    }
  }

  function clearFieldError(wrapper, fieldId) {
    const container = wrapper.querySelector(`[data-field="${fieldId}"]`)?.closest('.form-field');
    if (!container) return;
    container.classList.remove('has-error');
    container.querySelector('.field-error')?.remove();
  }

  function validateAllFields(wrapper, formId) {
    const data = getFormData(formId);
    let firstError = null;

    FIELDS.forEach(f => {
      if (!isFieldValid(f, data.fields[f.id])) {
        showFieldError(wrapper, f, data);
        if (!firstError) firstError = wrapper.querySelector(`[data-field="${f.id}"]`);
      } else {
        clearFieldError(wrapper, f.id);
      }
    });

    if (firstError) firstError.focus();
    return !firstError;
  }


  /* ================================================================
     4. FIELD RENDERING
     ================================================================ */

  function esc(str) {
    return (str || '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function renderSelectField(field, formId, value) {
    const options = field.options
      .map(o => `<option value="${esc(o)}"${value === o ? ' selected' : ''}>${o || 'Select ' + field.label.toLowerCase()}</option>`)
      .join('');
    return `<select name="${formId}-${field.id}" data-field="${field.id}">${options}</select>`;
  }

  function renderInputField(field, formId, value) {
    const attrs = [
      `type="${field.type}"`,
      `name="${formId}-${field.id}"`,
      `data-field="${field.id}"`,
      `value="${esc(value)}"`,
    ];
    if (field.placeholder) attrs.push(`placeholder="${field.placeholder}"`);
    if (field.pattern)     attrs.push(`pattern="${field.pattern}"`);
    if (field.maxlength)   attrs.push(`maxlength="${field.maxlength}"`);
    if (field.id === 'dob') attrs.push('inputmode="numeric"');
    return `<input ${attrs.join(' ')}>`;
  }

  function renderField(field, formId, data) {
    const value = data.fields[field.id] || '';
    const input = field.type === 'select'
      ? renderSelectField(field, formId, value)
      : renderInputField(field, formId, value);

    return `<div class="form-field"><label>${field.label} <span class="required">*</span></label>${input}</div>`;
  }


  /* ================================================================
     5. FORM HTML BUILDER
     ================================================================ */

  function buildHeader(wrapper) {
    const retreat    = esc(wrapper.dataset.retreat || '');
    const guestLabel = wrapper.dataset.guestLabel ? ' ' + esc(wrapper.dataset.guestLabel) : '';
    const retreatDate = esc(wrapper.dataset.retreatDate || '');
    const room       = esc(wrapper.dataset.room || '');
    const price      = esc(wrapper.dataset.price || '');

    return `
      <h3>Retreat Booking Information - ${retreat}${guestLabel}</h3>
      <p class="retreat-product-info">${retreatDate} / ${room} - ${price}</p>
    `;
  }

  function buildFieldRows(formId, data) {
    let html = '';
    let i = 0;

    while (i < FIELDS.length) {
      const field = FIELDS[i];
      const isDoubleCol = field.half && i + 1 < FIELDS.length && FIELDS[i + 1].half;

      if (isDoubleCol) {
        html += '<div class="retreat-form-grid retreat-form-grid--two-col">';
        html += renderField(field, formId, data);
        html += renderField(FIELDS[i + 1], formId, data);
        html += '</div>';
        i += 2;
      } else {
        html += '<div class="retreat-form-grid">';
        html += renderField(field, formId, data);
        html += '</div>';
        i++;
      }
    }
    return html;
  }

  function buildInfoDisplay(wrapper) {
    const items = [
      { label: 'Retreat',        value: wrapper.dataset.retreat },
      { label: 'Date of Retreat', value: wrapper.dataset.retreatDate },
      { label: 'Room Category',  value: wrapper.dataset.room },
      { label: 'Price',          value: wrapper.dataset.price },
    ];

    const inner = items
      .map(item => `
        <div class="retreat-info-item">
          <span class="retreat-info-label">${item.label}</span>
          <span class="retreat-info-value">${esc(item.value || '')}</span>
        </div>
      `).join('');

    return `<div class="retreat-info-display"><div class="retreat-info-grid">${inner}</div></div>`;
  }

  function buildTermsCheckbox(data) {
    return `
      <div class="retreat-form-grid"><div class="form-field form-field-checkbox">
        <label class="checkbox-label">
          <input type="checkbox" data-field="terms" ${data.termsAccepted ? 'checked' : ''}>
          <span class="checkbox-text"><span class="required">*</span> I agree to the
            <a href="${TERMS_LINKS.terms}" target="_blank">Terms and Conditions</a>,
            <a href="https://cdn.shopify.com/s/files/1/0976/9880/8195/files/standard_information_67531ddc-a0b6-4451-8268-ca635fcda44d.pdf?v=1771471086" target="_blank">Standard Information Sheet</a>,
            Travel Package, and
            Package Contract.
          </span>
        </label>
      </div></div>
    `;
  }

  function buildDownloadButton(formId) {
    return `
      <div class="pdf-download-section">
        <button type="button" class="pdf-download-btn" data-download ${isFormComplete(formId) ? '' : 'disabled'}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          Download Package Contract
        </button>
        <p class="pdf-info-text">📄 Download your completed Package Holiday Contract with all your booking details.</p>
      </div>
    `;
  }

  function buildFormHTML(wrapper) {
    const formId = wrapper.dataset.formId;
    const data   = getFormData(formId);

    return [
      buildHeader(wrapper),
      '<div class="retreat-form">',
      buildFieldRows(formId, data),
      buildInfoDisplay(wrapper),
      buildTermsCheckbox(data),
      buildDownloadButton(formId),
      '</div>',
    ].join('');
  }


  /* ================================================================
     6. EVENT HANDLING
     ================================================================ */

  function formatDobInput(el) {
    const raw = el.value.replace(/[^\d]/g, '').slice(0, 8);
    let formatted = '';
    for (let i = 0; i < raw.length; i++) {
      if (i === 2 || i === 4) formatted += '.';
      formatted += raw[i];
    }
    el.value = formatted;
  }

  function updateFieldValue(el, formId, fieldId) {
    const data = getFormData(formId);
    if (data.fields[fieldId] === el.value) return;

    data.fields[fieldId] = el.value;

    if (data.termsAccepted) {
      data.termsAccepted = false;
      const terms = el.closest(FORM_SELECTOR)?.querySelector('[data-field="terms"]');
      if (terms) terms.checked = false;
      syncCartAttributes(formId, true);
    }
    saveState();
  }

  function bindEvents(wrapper, formId) {
    const dlBtn = wrapper.querySelector('[data-download]');

    function refreshUI() {
      if (dlBtn) dlBtn.disabled = !isFormComplete(formId);
      updateCheckoutButton();
    }

    wrapper.querySelectorAll('[data-field]').forEach(el => {
      const fieldId = el.dataset.field;

      if (fieldId === 'terms') {
        el.addEventListener('change', () => handleTermsChange(el, wrapper, formId, refreshUI));
        return;
      }

      if (fieldId === 'dob') {
        el.addEventListener('input', () => {
          formatDobInput(el);
          updateFieldValue(el, formId, fieldId);
          const fieldDef = FIELDS.find(f => f.id === fieldId);
          if (fieldDef && isFieldValid(fieldDef, el.value)) clearFieldError(wrapper, fieldId);
          refreshUI();
        });
        el.addEventListener('change', e => e.stopPropagation());
        return;
      }

      el.addEventListener('change', () => {
        updateFieldValue(el, formId, fieldId);
        const fieldDef = FIELDS.find(f => f.id === fieldId);
        if (fieldDef && isFieldValid(fieldDef, el.value)) clearFieldError(wrapper, fieldId);
        refreshUI();
      });
    });

    dlBtn?.addEventListener('click', () => handleDownload(wrapper, formId));
  }

  function handleTermsChange(el, wrapper, formId, refreshUI) {
    const data = getFormData(formId);

    if (el.checked) {
      const allValid = validateAllFields(wrapper, formId);
      if (!allValid) {
        el.checked = false;
        return;
      }
    }

    data.termsAccepted = el.checked;
    saveState();
    syncCartAttributes(formId, !el.checked);
    refreshUI();
  }


  /* ================================================================
     7. PDF / DOWNLOAD
     ================================================================ */

  function escHtml(text) {
    const div = document.createElement('div');
    div.textContent = text || '';
    return div.innerHTML;
  }

  function handleDownload(wrapper, formId) {
    if (!isFormComplete(formId)) return;

    const data = getFormData(formId);
    const btn  = wrapper.querySelector('[data-download]');
    btn.disabled = true;
    btn.textContent = 'Generating…';

    const templateUrl = window.RetreatBookingConfig?.contractTemplateUrl?.split('?')[0];
    if (!templateUrl) {
      alert('Template not configured');
      resetDownloadBtn(btn);
      return;
    }

    fetch(templateUrl)
      .then(r => r.text())
      .then(html => {
        const replacements = {
          'first-name':       data.fields['first-name'],
          'last-name':        data.fields['last-name'],
          'address':          data.fields['address'],
          'zip-town-country': `${data.fields['zip']} ${data.fields['city']}, ${data.fields['country']}`,
          'date-of-birth':    data.fields['dob'],
          'email':            data.fields['email'],
          'phone':            data.fields['phone'],
          'retreat':          wrapper.dataset.retreat || '',
          'date-of-retreat':  wrapper.dataset.retreatDate || '',
          'room-category':    wrapper.dataset.room || '',
          'price':            wrapper.dataset.price || '',
        };

        for (const [key, val] of Object.entries(replacements)) {
          html = html.replace(
            new RegExp(`(data-dynamic-content="${key}"[^>]*>[^<]*<strong>)\\[__CONTENT__\\]`, 'g'),
            `$1${escHtml(val)}`
          );
        }

        const blob = new Blob([html], { type: 'text/html' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `Contract_${data.fields['last-name'].replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.html`;
        link.click();
        URL.revokeObjectURL(link.href);

        btn.textContent = '✓ Downloaded';
        setTimeout(() => resetDownloadBtn(btn), 2000);
      })
      .catch(() => {
        alert('Download failed');
        resetDownloadBtn(btn);
      });
  }

  function resetDownloadBtn(btn) {
    btn.disabled = false;
    btn.textContent = 'Download Package Contract';
  }


  /* ================================================================
     8. CART SYNC
     ================================================================ */

  let cartSyncTimer = null;

  function syncCartAttributes(formId, clear) {
    const data = getFormData(formId);
    const attrs = {};

    FIELDS.forEach(f => {
      attrs[`${formId}_${f.label}`] = clear ? '' : data.fields[f.id];
    });
    attrs[`${formId}_Agreed to Terms`] = (!clear && data.termsAccepted) ? 'Yes' : '';

    clearTimeout(cartSyncTimer);
    cartSyncTimer = setTimeout(() => {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attrs }),
      });
    }, CART_SYNC_DELAY);
  }

  function updateCheckoutButton() {
    const btn = document.getElementById('checkout');
    if (!btn) return;

    const wrappers = document.querySelectorAll(FORM_SELECTOR);
    const allComplete = wrappers.length === 0 || [...wrappers].every(w => isFormComplete(w.dataset.formId));
    btn.disabled = !allComplete;
  }


  /* ================================================================
     9. DOM RENDERING & LIFECYCLE
     ================================================================ */

  function getWrapContainer() {
    return document.querySelector(WRAPPER_SELECTOR);
  }

  function renderAll(cleanOrphans) {
    const container = getWrapContainer();
    if (!container) return;

    const wrappers = container.querySelectorAll(FORM_SELECTOR);
    const activeIds = new Set();

    wrappers.forEach(wrapper => {
      const formId = wrapper.dataset.formId;
      if (!formId) return;
      activeIds.add(formId);

      if (wrapper.children.length === 0) {
        wrapper.innerHTML = buildFormHTML(wrapper);
        bindEvents(wrapper, formId);
      }
    });

    if (cleanOrphans) {
      const state = getState();
      Object.keys(state.forms).forEach(id => {
        if (!activeIds.has(id)) {
          syncCartAttributes(id, true);
          removeFormData(id);
        }
      });
    }

    updateCheckoutButton();
  }

  function onCartUpdate() {
    let attempts = 0;

    const poll = () => {
      const container = getWrapContainer();
      const wrappers = container ? container.querySelectorAll(FORM_SELECTOR) : [];

      if (wrappers.length || attempts++ >= POLL_MAX_ATTEMPTS) {
        renderAll(false);
      } else {
        setTimeout(poll, POLL_BASE_DELAY + attempts * POLL_INCREMENT);
      }
    };

    setTimeout(poll, POLL_BASE_DELAY);
  }

  function init() {
    STATE = null;
    getState();
    renderAll(true);
    document.addEventListener('cart:updated', onCartUpdate);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();